/**
 * BoundariesTab.tsx — `boundaries.rules[]` (ADR-084 D1), the agentic
 * editor's Boundaries tab (design doc §Boundaries tab, E12-S3a).
 *
 * NOT reorderable — `boundaries.rules[]` has no D4 ordering rule and the
 * design doc doesn't name reorder for it — a plain repeatable list: add
 * row / remove row / edit `id` (slug) and `text` (empty → the `id` renders,
 * per D1 `text: str | None`, matching today's fallback behaviour).
 */

import { useTranslation } from 'react-i18next';
import {
  AGENT_CONFIG_MAX_LENGTHS,
  type BoundaryRule,
  type SkillVersionFieldError,
} from '../../api/types';
import { CounterField, textActionClass, textActionDangerClass } from './agenticFieldHelpers';
import { inputClass } from '../../components/Field';

export interface BoundariesTabProps {
  rules: BoundaryRule[];
  onChange: (next: BoundaryRule[]) => void;
  disabled?: boolean;
  fieldErrors: SkillVersionFieldError[];
}

const errorFor = (fieldErrors: SkillVersionFieldError[], path: string): string | undefined =>
  fieldErrors.find((error) => error.field_path === path)?.reason;

export const BoundariesTab = ({
  rules,
  onChange,
  disabled = undefined,
  fieldErrors,
}: BoundariesTabProps) => {
  const { t } = useTranslation();

  const updateRule = (index: number, patch: Partial<BoundaryRule>) => {
    onChange(rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));
  };
  const removeRule = (index: number) => onChange(rules.filter((_, i) => i !== index));
  const addRule = () => onChange([...rules, { id: '', text: '' }]);

  return (
    <section aria-labelledby="agentic-boundaries-heading" className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 id="agentic-boundaries-heading" className="text-h3 text-ink-1">
          {t('config.editAgentic.boundaries.heading')}
        </h3>
        <button
          type="button"
          data-testid="agentic-boundaries-add"
          disabled={disabled}
          onClick={addRule}
          className={textActionClass}
        >
          {t('config.editAgentic.boundaries.add')}
        </button>
      </div>

      {rules.length === 0 ? (
        <p data-testid="agentic-boundaries-empty" className="text-body text-ink-2">
          {t('config.editAgentic.boundaries.empty')}
        </p>
      ) : (
        rules.map((rule, index) => (
          <div
            // Rules have no stable id until the operator sets one; index is
            // fine here since rows are never reordered (no reorder control
            // on this tab) and additions always append.
            // eslint-disable-next-line react/no-array-index-key -- rows have no stable id; revisit in slice 11
            key={index}
            data-testid={`agentic-boundary-${index}`}
            className="flex flex-col gap-2 rounded-lg bg-surface-2 p-3 ring-1 ring-inset ring-ink-3/10"
          >
            <div className="flex items-center justify-between gap-2">
              <label
                htmlFor={`agentic-boundary-${index}-id`}
                className="text-meta font-medium text-ink-2"
              >
                {t('config.editAgentic.boundaries.id')}
              </label>
              <button
                type="button"
                data-testid={`agentic-boundary-${index}-remove`}
                disabled={disabled}
                onClick={() => removeRule(index)}
                className={textActionDangerClass}
              >
                {t('config.editAgentic.boundaries.remove')}
              </button>
            </div>
            <input
              id={`agentic-boundary-${index}-id`}
              data-testid={`agentic-boundary-${index}-id`}
              type="text"
              value={rule.id}
              disabled={disabled}
              onChange={(event) => updateRule(index, { id: event.target.value })}
              className={inputClass}
            />
            <CounterField
              id={`agentic-boundary-${index}-text`}
              label={t('config.editAgentic.boundaries.text')}
              value={rule.text ?? ''}
              maxLength={AGENT_CONFIG_MAX_LENGTHS.boundaryRuleText}
              onChange={(next) => updateRule(index, { text: next })}
              disabled={disabled}
              multiline
              rows={3}
              error={errorFor(fieldErrors, `boundaries.rules.${index}.text`)}
            />
          </div>
        ))
      )}
    </section>
  );
};
